// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { useEvolveGet } from '@evolvemep/api-ts';

import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import type { VDCOSChartOptionalFilters, VDCOSChartFilters } from 'modules/Shop/Insights/RevitUsage/useChartFilters';

export const useFilterData = (
  { toolType, timeFrame, selectedRevitYear }: VDCOSChartFilters,
  setFilters: (updatedFilters: Partial<VDCOSChartOptionalFilters>) => void,
) => {
  const {
    data: revitVersions,
    loading: loadingRevitYears,
    setDefaultOpts: setDefaultOptsRevitVersions,
  } = useEvolveGet(`usage-stats/v1/reporting/versions/revit`, {
    alertOnError: true,
  });
  const params = useMemo(
    () => ({ toolType, ...convertTimeFrameDataToRequestParams(timeFrame) }),
    [timeFrame, toolType],
  );
  useEffect(() => {
    setDefaultOptsRevitVersions({
      lazy: false,
      defaultConfig: {
        params,
      },
    });
  }, [params, setDefaultOptsRevitVersions]);
  const revitYears = useMemo(
    () => [...new Set(revitVersions?.map((r) => r.revitYear))].toSorted((a, b) => b.localeCompare(a)),
    [revitVersions],
  );
  useEffect(() => {
    if (!selectedRevitYear || !revitYears.includes(selectedRevitYear)) {
      setFilters({
        selectedRevitYear: revitYears[0] ?? null,
      });
    }
  }, [selectedRevitYear, revitYears, setFilters]);

  const {
    data: viableUsers,
    setDefaultOpts: setDefaultOptsUsers,
    loading: loadingUsers,
  } = useEvolveGet('usage-stats/v1/reporting/events/viable/users');
  useEffect(() => {
    setDefaultOptsUsers({
      lazy: false,
      defaultConfig: {
        params,
      },
    });
  }, [params, setDefaultOptsUsers]);

  const {
    data: featureTypes,
    setDefaultOpts: setDefaultOptsFeatureTypes,
    loading: loadingFeatureTypes,
  } = useEvolveGet('usage-stats/v1/reporting/events/viable/features');
  useEffect(() => {
    setDefaultOptsFeatureTypes({
      lazy: false,
      defaultConfig: {
        params,
      },
    });
  }, [params, setDefaultOptsFeatureTypes]);
  const viableFeatureTypeNames = useMemo(() => featureTypes?.map((f) => f.featureTypeName) ?? [], [featureTypes]);

  return {
    revitYears,
    loadingRevitYears,
    viableFeatureTypeNames,
    loadingFeatureTypes,
    viableUsers,
    loadingUsers,
  };
};
